import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import { Candidate, PollResponse, Precinct } from '../../types';
import React, { useEffect, useState } from 'react';
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  query,
  setDoc
} from 'firebase/firestore';

import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';
import { v4 as uuidv4 } from 'uuid';

interface CandidateWithId extends Candidate {
  id: string;
}

const ReplicateVote: React.FC = () => {
  const [candidates, setCandidates] = useState<CandidateWithId[]>([]);
  const [sourceCandidateId, setSourceCandidateId] = useState<string>('');
  const [targetCandidateId, setTargetCandidateId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const candidatesRef = collection(db, 'candidates');
        const q = query(candidatesRef);
        const querySnapshot = await getDocs(q);
        const fetchedCandidates = querySnapshot.docs.map(candidate => ({
          ...candidate.data() as Candidate,
          id: candidate.id
        } as CandidateWithId));
        setCandidates(fetchedCandidates);
      } catch (error) {
        console.error('Error fetching candidates:', error);
      }
    };

    fetchCandidates();
  }, []);

  const handleSourceCandidateChange = (event: SelectChangeEvent) => {
    setSourceCandidateId(event.target.value as string);
  };

  const handleTargetCandidateChange = (event: SelectChangeEvent) => {
    setTargetCandidateId(event.target.value as string);
  };

  const handleReplicateVote = async () => {
    debugger;
    
    if (!sourceCandidateId || !targetCandidateId) {
      alert('Please select both source and target candidates');
      return;
    }

    if (!user) {
      alert('You must be logged in to replicate votes');
      return;
    }

    setLoading(true);

    try {
      // Find source and target candidate details
      const sourceCandidate = candidates.find(c => c.id === sourceCandidateId);
      const targetCandidate = candidates.find(c => c.id === targetCandidateId);

      if (!sourceCandidate || !targetCandidate) {
        throw new Error('Candidate not found');
      }

      // Track number of replicated votes
      let replicatedVotesCount = 0;

      const dashboard_id = 'SURIGAO DEL NORTE_DISTRICT 1';

      const precinctsRef = collection(db, "precincts")
      const q = query(precinctsRef);
      const querySnapshot = await getDocs(q);

      for (const precinctDoc of querySnapshot.docs) {
        const precinct = precinctDoc.data() as Precinct;

        const tabPrecinctVotersColPath =
          `tabulationsV2/${dashboard_id}/regions/${precinct.region}/`
          + `provinces/${precinct.province}/districts/${precinct.district}/`
          + `cities/${precinct.city_municipality}/barangays/${precinct.barangay}/`
          + `precincts/${precinct.precinct}/voters`;

        const tabPrecinctVotersColRef = collection(db, tabPrecinctVotersColPath);
        const tabPrecinctVotersQuery = query(tabPrecinctVotersColRef);
        const tabPrecinctVotersQuerySnapshot = await getDocs(tabPrecinctVotersQuery);

        // Process each poll response
        for (const resp of tabPrecinctVotersQuerySnapshot.docs) {
          const pollResponse = resp.data() as PollResponse;
          
          // Check if any response value matches the source candidate's name
          const matchingResponses = Object.entries(pollResponse.responses)
            .filter(([_, value]) => value === sourceCandidate.name);

          if (matchingResponses.length > 0) {
            // Check if target candidate already has votes for these questions
            const targetAlreadyVoted = matchingResponses.some(([key]) => 
              pollResponse.responses[key] === targetCandidate.name
            );

            if (targetAlreadyVoted) {
              continue; // Skip this poll response
            }

            // Create new responses with target candidate
            const newResponses = {
              ...pollResponse.responses,
              [targetCandidate.position]: targetCandidate.name
            };
            
            // Create a new poll response document
            const responseId = uuidv4();
            const newPollResponse: PollResponse = {
              ...pollResponse,
              id: responseId,
              responses: newResponses,
              submittedBy: user.display_name ?? user.email ?? user.uid,
              submittedAt: Timestamp.now(),
            };
            console.log(newPollResponse);
            
            // Save the new poll response
            const responseRef = doc(collection(db, 'pollResponses'), responseId);
            await setDoc(responseRef, newPollResponse);
            
            replicatedVotesCount++;
          }
        }
      }

      alert(`Vote replication completed. ${replicatedVotesCount} new votes replicated.`);
    } catch (error) {
      console.error('Error replicating votes:', error);
      alert('Failed to replicate votes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Replicate Vote
        </Typography>
        <Box sx={{ '& > :not(style)': { m: 1, width: '100%' } }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Source Candidate</InputLabel>
            <Select
              value={sourceCandidateId}
              label="Source Candidate"
              onChange={handleSourceCandidateChange}
            >
              {candidates.map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.id}>
                  {candidate.name} - {candidate.position}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel>Target Candidate</InputLabel>
            <Select
              value={targetCandidateId}
              label="Target Candidate"
              onChange={handleTargetCandidateChange}
            >
              {candidates.map((candidate) => (
                <MenuItem key={candidate.id} value={candidate.id}>
                  {candidate.name} - {candidate.position}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleReplicateVote}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Replicating...' : 'Replicate Vote'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default ReplicateVote;
